import { useEffect, useState } from "react";
import { Sparkles, Wrench, Bug, Compass } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Permanent release-notes page. Opening this page also marks the latest
 * release as seen (server + localStorage) so the footer NEW dot clears.
 */
export default function WhatsNew() {
  const { user, refresh } = useAuth();
  const [releases, setReleases] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/release-notes");
        if (cancelled) return;
        setReleases(data?.releases || []);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Mark latest release as VIEWED (not dismissed) — clears the footer dot
  // but keeps the modal firing until the user explicitly dismisses it via
  // "Got it".
  useEffect(() => {
    if (!releases || releases.length === 0) return;
    const latest = releases[0];
    if (user && typeof user === "object" && user.email) {
      (async () => {
        try {
          await api.post(`/release-notes/viewed/${encodeURIComponent(latest.version)}`);
          await refresh();
        } catch { /* best-effort */ }
      })();
    }
  }, [releases, user, refresh]);

  return (
    <div className="min-h-screen bg-ivory">
      <div className="max-w-3xl mx-auto px-5 py-10 space-y-6 pb-16" data-testid="whats-new-page">
        <header>
          <div className="label-overline inline-flex items-center gap-1.5"><Sparkles size={11} /> What's new</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Release notes</h1>
          <p className="text-ink-600 text-sm mt-2 max-w-2xl">
            AMDS is in public beta. Every release we ship shows up here — new features, quiet fixes, and what we're building next.
          </p>
        </header>

        {error && (
          <div className="card-base p-6 text-sm text-ink-600" data-testid="whats-new-error">
            Release notes are temporarily unavailable. The rest of AMDS is unaffected.
          </div>
        )}

        {releases && releases.length === 0 && !error && (
          <div className="card-base p-6 text-sm text-ink-600" data-testid="whats-new-empty">
            No releases yet.
          </div>
        )}

        {releases && releases.length > 0 && (
          <ol className="space-y-4">
            {releases.map((r, idx) => (
              <ReleaseCard release={r} isLatest={idx === 0} key={r.version} />
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function ReleaseCard({ release, isLatest }) {
  const s = release.sections || {};
  const dateLabel = release.release_date
    ? new Date(release.release_date).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })
    : null;
  return (
    <li>
      <article
        className={`card-base p-6 ${isLatest ? "ring-1 ring-sage-500/40" : ""}`}
        data-testid={`release-item-${release.version}`}
      >
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="font-heading text-xl font-semibold tracking-tight">{release.title || `Release ${release.version}`}</h2>
          <span className="text-xs text-ink-500 uppercase tracking-wider">v{release.version}</span>
          {dateLabel && <span className="text-xs text-ink-500">· {dateLabel}</span>}
          {isLatest && <span className="pill bg-sage-100 text-sage-700 !text-[10px]" data-testid="release-latest-badge">Latest</span>}
        </div>
        <div className="mt-4 space-y-4 text-sm">
          <Section icon={<Sparkles size={14} />} label="New" items={s.new_features} tone="sage" />
          <Section icon={<Wrench size={14} />} label="Improvements" items={s.improvements} tone="ink" />
          <Section icon={<Bug size={14} />} label="Bug fixes" items={s.bug_fixes} tone="muted" />
          <Section icon={<Compass size={14} />} label="Coming soon" items={s.coming_soon} tone="amber" />
        </div>
      </article>
    </li>
  );
}

function Section({ icon, label, items, tone }) {
  if (!items || items.length === 0) return null;
  const toneClass = {
    sage: "text-sage-700",
    ink: "text-ink-800",
    muted: "text-ink-500",
    amber: "text-amber-deep",
  }[tone] || "text-ink-700";
  const bulletClass = {
    sage: "bg-sage-500",
    ink: "bg-ink-500",
    muted: "bg-ink-300",
    amber: "bg-amber-deep",
  }[tone] || "bg-ink-500";
  return (
    <div>
      <div className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${toneClass}`}>{icon} {label}</div>
      <ul className="mt-2 space-y-1.5">
        {items.map((line, i) => (
          <li key={i} className="flex items-start gap-2 text-ink-700 leading-relaxed">
            <span className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${bulletClass}`} />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
